import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDate, formatDateTime } from '@utils/dateUtils'
import { Box, Chip, FormControl, InputLabel, Select, MenuItem, Stack } from '@mui/material'
import { Add, FilterList, Clear } from '@mui/icons-material'
import { PageHeader, Button, DataTable, StatusChip, Card, PermissionGate, ExportButtons } from '@components/common'
import { WorkOrderForm } from '@components/maintenance'
import { toast } from 'react-toastify'
import api from '@services/api'

const priorityColors = {
  low: 'default',
  medium: 'info',
  high: 'warning',
  critical: 'error',
}

const columns = [
  { field: 'work_order_number', headerName: 'WO #', sortable: true, width: 120 },
  { field: 'title', headerName: 'Title', sortable: true },
  { field: 'asset_name', headerName: 'Asset', sortable: true },
  {
    field: 'priority',
    headerName: 'Priority',
    sortable: true,
    renderCell: (value) => (
      <Chip
        label={value}
        size="small"
        color={priorityColors[value] || 'default'}
        sx={{ textTransform: 'capitalize' }}
      />
    ),
  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: true,
    renderCell: (value) => <StatusChip status={value} />,
  },
  {
    field: 'due_date',
    headerName: 'Due Date',
    sortable: true,
    renderCell: (value) => value ? formatDate(value) : '-',
  },
  { field: 'assigned_to_name', headerName: 'Assigned To', sortable: true },
]

const WorkOrdersList = () => {
  const navigate = useNavigate()
  const [workOrders, setWorkOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [showFilters, setShowFilters] = useState(false)

  // Pagination state
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [totalCount, setTotalCount] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')

  // Filter states
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    work_type: '',
    assigned_to: '',
  })

  // Filter options
  const [technicians, setTechnicians] = useState([])

  // Refs for stable callbacks
  const filtersRef = useRef(filters)
  const searchTermRef = useRef(searchTerm)
  const pageSizeRef = useRef(pageSize)

  // Export columns
  const exportColumns = useMemo(() => [
    { field: 'work_order_number', headerName: 'WO #' },
    { field: 'title', headerName: 'Title' },
    { field: 'asset_name', headerName: 'Asset' },
    { field: 'category', headerName: 'Category', renderValue: (v) => v?.replace(/_/g, ' ') },
    { field: 'work_type', headerName: 'Work Type', renderValue: (v) => v?.replace(/_/g, ' ') },
    { field: 'priority', headerName: 'Priority' },
    { field: 'status', headerName: 'Status', renderValue: (v) => v?.replace(/_/g, ' ').toUpperCase() },
    { field: 'due_date', headerName: 'Due Date', renderValue: (v) => v ? formatDate(v) : '' },
    { field: 'assigned_to_name', headerName: 'Assigned To' },
    { field: 'department_name', headerName: 'Section/Unit' },
  ], [])

  // Keep refs in sync
  useEffect(() => { filtersRef.current = filters }, [filters])
  useEffect(() => { searchTermRef.current = searchTerm }, [searchTerm])
  useEffect(() => { pageSizeRef.current = pageSize }, [pageSize])

  const fetchWorkOrders = useCallback(async (currentPage, currentPageSize, search) => {
    try {
      setLoading(true)
      const currentFilters = filtersRef.current
      const params = {
        page: currentPage + 1,
        page_size: currentPageSize,
      }
      if (search) params.search = search
      if (currentFilters.status) params.status = currentFilters.status
      if (currentFilters.priority) params.priority = currentFilters.priority
      if (currentFilters.work_type) params.work_type = currentFilters.work_type
      if (currentFilters.assigned_to) params.assigned_to = currentFilters.assigned_to

      const response = await api.get('/maintenance/work-orders/', { params })
      setWorkOrders(response.data.results || response.data)
      setTotalCount(response.data.count || 0)
    } catch (error) {
      console.error('Error fetching work orders:', error)
      toast.error('Failed to load work orders')
    } finally {
      setLoading(false)
    }
  }, [])

  const handlePaginationChange = useCallback(({ page: newPage, pageSize: newPageSize }) => {
    setPage(newPage)
    setPageSize(newPageSize)
    fetchWorkOrders(newPage, newPageSize, searchTermRef.current)
  }, [fetchWorkOrders])

  const handleSearch = useCallback((search) => {
    setSearchTerm(search)
    setPage(0)
    fetchWorkOrders(0, pageSizeRef.current, search)
  }, [fetchWorkOrders])

  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/common/users/', { params: { role: 'technician', page_size: 100 } })
      setTechnicians(response.data.results || response.data || [])
    } catch (error) {
      console.error('Error fetching filter options:', error)
    }
  }

  // Initial load and when filters change
  useEffect(() => {
    setPage(0)
    setSearchTerm('')
    fetchWorkOrders(0, pageSizeRef.current, '')
  }, [filters, fetchWorkOrders])

  useEffect(() => {
    fetchFilterOptions()
  }, [])

  const handleFilterChange = (field) => (event) => {
    setFilters(prev => ({ ...prev, [field]: event.target.value }))
  }

  const clearFilters = () => {
    setFilters({ status: '', priority: '', work_type: '', assigned_to: '' })
  }

  const hasActiveFilters = filters.status || filters.priority || filters.work_type || filters.assigned_to

  const getActiveFilterLabels = () => {
    const labels = {}
    if (filters.status) labels.Status = filters.status.replace(/_/g, ' ')
    if (filters.priority) labels.Priority = filters.priority
    if (filters.work_type) labels['Work Type'] = filters.work_type.replace(/_/g, ' ')
    if (filters.assigned_to) {
      const tech = technicians.find(t => t.id === parseInt(filters.assigned_to))
      labels['Assigned To'] = tech ? `${tech.first_name} ${tech.last_name}` : filters.assigned_to
    }
    return labels
  }

  const handleFormSuccess = () => {
    fetchWorkOrders(page, pageSize, searchTerm)
  }

  return (
    <Box>
      <PageHeader
        title="Work Orders"
        subtitle="Manage maintenance work orders"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Work Orders' },
        ]}
        actions={
          <>
            <ExportButtons
              data={workOrders}
              columns={exportColumns}
              title="Work Orders List"
              filename="work_orders"
              filters={getActiveFilterLabels()}
            />
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => setShowFilters(!showFilters)}
              color={hasActiveFilters ? 'primary' : 'inherit'}
            >
              Filters {hasActiveFilters && `(${Object.values(filters).filter(Boolean).length})`}
            </Button>
            <PermissionGate permission="work_orders.create">
              <Button startIcon={<Add />} onClick={() => setFormOpen(true)}>
                Create Work Order
              </Button>
            </PermissionGate>
          </>
        }
      />

      {/* Filters Section */}
      {showFilters && (
        <Card sx={{ mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={handleFilterChange('status')}
                label="Status"
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="on_hold">On Hold</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Priority</InputLabel>
              <Select
                value={filters.priority}
                onChange={handleFilterChange('priority')}
                label="Priority"
              >
                <MenuItem value="">All Priorities</MenuItem>
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Work Type</InputLabel>
              <Select
                value={filters.work_type}
                onChange={handleFilterChange('work_type')}
                label="Work Type"
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="corrective">Corrective</MenuItem>
                <MenuItem value="preventive">Preventive</MenuItem>
                <MenuItem value="predictive">Predictive</MenuItem>
                <MenuItem value="emergency">Emergency</MenuItem>
                <MenuItem value="installation">Installation</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Assigned To</InputLabel>
              <Select
                value={filters.assigned_to}
                onChange={handleFilterChange('assigned_to')}
                label="Assigned To"
              >
                <MenuItem value="">All Technicians</MenuItem>
                {technicians.map(tech => (
                  <MenuItem key={tech.id} value={tech.id}>
                    {tech.first_name} {tech.last_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {hasActiveFilters && (
              <Button
                variant="text"
                startIcon={<Clear />}
                onClick={clearFilters}
                size="small"
              >
                Clear
              </Button>
            )}
          </Stack>

          {/* Active filter chips */}
          {hasActiveFilters && (
            <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
              {Object.entries(getActiveFilterLabels()).map(([key, value]) => (
                <Chip
                  key={key}
                  label={`${key}: ${value}`}
                  size="small"
                  onDelete={() => {
                    const fieldMap = { 'Status': 'status', 'Priority': 'priority', 'Work Type': 'work_type', 'Assigned To': 'assigned_to' }
                    setFilters(prev => ({ ...prev, [fieldMap[key]]: '' }))
                  }}
                />
              ))}
            </Stack>
          )}
        </Card>
      )}

      <Card>
        <DataTable
          columns={columns}
          data={workOrders}
          loading={loading}
          onRowClick={(row) => navigate(`/work-orders/${row.id}`)}
          searchPlaceholder="Search work orders..."
          emptyMessage="No work orders found"
          serverSide
          totalCount={totalCount}
          page={page}
          rowsPerPage={pageSize}
          onPaginationChange={handlePaginationChange}
          onSearch={handleSearch}
        />
      </Card>

      <WorkOrderForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSuccess={handleFormSuccess}
      />
    </Box>
  )
}

export default WorkOrdersList
